import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CircularProgress from "@mui/material/CircularProgress";
import ReferenceImagePreviewList from "./ReferenceImagePreviewList";

import dayjs from "dayjs";
import formatCartItemDetails from "../../../utils/formatCartItemDetails";
import formatCategoryLabel from "../../../utils/formatCategoryLabel";

import {
  isDelivery,
  normalizeLocation,
  deliveryAddressString,
} from "../../../utils/fulfillment";

const BookingAccordionMobile = ({ orders, onDelete, onSendConfirmation }) => {
  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOrderId, setMenuOrderId] = useState(null);
  const [sendingOrderId, setSendingOrderId] = useState(null);

  const navigate = useNavigate();

  const handleExpand = (panel) => (e, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleMenuClick = (event, orderId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuOrderId(orderId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuOrderId(null);
  };

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>
      {orders.map((order, idx) => {
        const latestEdit =
          Array.isArray(order.edits) && order.edits.length > 0
            ? order.edits[order.edits.length - 1].orderSnapshot
            : null;

        const isEdited = Array.isArray(order.edits) && order.edits.length > 0;

        const customer = latestEdit?.customer || order.customer;
        const fulfillment = latestEdit?.fulfillment || order.fulfillment;
        const payment = latestEdit?.payment || order.payment;
        const meta = latestEdit?.meta || order.meta;
        const items = latestEdit?.items || order.items;
        const internal = latestEdit?.internal || order.internal;

        const orderDate = dayjs(meta.createdAt).format("MMM D, YYYY");
        const pickupDate = dayjs(fulfillment.date).format("MMM D, YYYY");
        const isToday = dayjs(fulfillment.date).isSame(dayjs(), "day");

        const delivery = isDelivery(fulfillment);
        const storeLocation = normalizeLocation(fulfillment);
        const addressStr = deliveryAddressString(fulfillment);

        return (
          <Accordion
            key={idx}
            expanded={expanded === idx}
            onChange={handleExpand(idx)}
            sx={isToday ? { backgroundColor: "#FFFBE6" } : {}}>
            <AccordionSummary>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: 1,
                }}>
                <Box>
                  <Typography fontWeight={600} sx={{ maxWidth: 180 }}>
                    {customer.firstName} {customer.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {pickupDate} at {fulfillment.time}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {storeLocation}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {delivery && (
                    <Chip
                      size="small"
                      icon={<LocalShippingIcon sx={{ fontSize: 16 }} />}
                      label="Delivery"
                      color="info"
                      variant="outlined"
                    />
                  )}

                  {meta.status === "in-progress" && (
                    <>
                      <IconButton
                        onClick={(e) => handleMenuClick(e, order._id)}
                        size="small"
                        aria-label="more actions">
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && menuOrderId === order._id}
                        onClose={handleMenuClose}
                        onClick={(e) => e.stopPropagation()}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}>
                        <MenuItem
                          onClick={() => {
                            navigate(`/admin/orders/${order._id}/edit`);
                            handleMenuClose();
                          }}>
                          <ListItemIcon>
                            <EditIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Edit Order" />
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            onDelete(order._id);
                            handleMenuClose();
                          }}>
                          <ListItemIcon>
                            <DeleteIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Delete Order" />
                        </MenuItem>
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setSendingOrderId(order._id);
                            onSendConfirmation(order._id).finally(() => {
                              setSendingOrderId(null);
                              handleMenuClose();
                            });
                          }}>
                          <ListItemIcon>
                            {sendingOrderId === order._id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <EmailIcon />
                            )}
                          </ListItemIcon>
                          <ListItemText primary="Send Confirmation" />
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">
                  Order #{meta.orderId}
                </Typography>
                {isEdited && (
                  <Chip label="Edited" size="small" color="warning" />
                )}
              </Box>

              <Typography variant="body2" gutterBottom>
                Order Date: {orderDate}
              </Typography>

              <Typography variant="body2" gutterBottom>
                {delivery
                  ? `Fulfillment: delivery at ${
                      addressStr || "(address unavailable)"
                    }`
                  : `Fulfillment: pickup at ${storeLocation}`}
              </Typography>

              <Typography
                variant="subtitle1"
                mt={2}
                mb={1}
                sx={{ fontWeight: "bold" }}>
                Items
              </Typography>
              {items.map((item, i) => {
                const details = formatCartItemDetails(item);
                const categoryLabel = formatCategoryLabel(item);

                return (
                  <Box key={i} sx={{ display: "flex", mb: 2 }}>
                    <Avatar
                      variant="rounded"
                      src={item.image}
                      alt={item.displayName || item.name}
                      sx={{ width: 60, height: 60, mr: 2 }}
                    />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary"
                        sx={{ fontSize: 15 }}>
                        {categoryLabel}
                      </Typography>
                      <Typography variant="body2">
                        Qty: {item.quantity} — ${item.totalPrice.toFixed(2)}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          mt: 0.5,
                        }}>
                        {details.map(({ label, value }, j) => (
                          <Chip
                            key={j}
                            label={`${label}: ${value}`}
                            size="small"
                            sx={{
                              alignSelf: "flex-start",
                              justifyContent: "flex-start",
                              textAlign: "left",
                              fontSize: "12px",
                              whiteSpace: "normal",
                              textWrap: "wrap",
                              maxWidth: 200,
                            }}
                          />
                        ))}
                      </Box>
                      {item?.note && (
                        <Box mt={1} ml={1}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontStyle: "italic",
                            }}>
                            Note: {item.note}
                          </Typography>
                        </Box>
                      )}
                      {Array.isArray(item.referenceImages) &&
                        item.referenceImages.length > 0 && (
                          <Box mt={2}>
                            <ReferenceImagePreviewList
                              images={item.referenceImages}
                            />
                          </Box>
                        )}
                    </Box>
                  </Box>
                );
              })}

              {internal?.kitchenNotes && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Kitchen Notes
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}>
                    {internal.kitchenNotes}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 0.5, fontWeight: "bold" }}>
                  Customer
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {customer.firstName} {customer.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {customer.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {customer.phone}
                </Typography>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 0.5, fontWeight: "bold" }}>
                  Payment
                </Typography>
                <Typography variant="body2">
                  Subtotal: ${payment.subtotal.toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  Tax: ${payment.tax.toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  Total: ${payment.total.toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  Paid: {payment.paid ? "Yes" : "No"}
                </Typography>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Status
                </Typography>
                <Typography variant="body2">{meta.status}</Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default BookingAccordionMobile;
